import React from "react";

import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import {
  alpha,
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  Link,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";

import { useAuth } from "src/domains/auth/module/hooks/useAuth";
import { AUTH } from "src/platform/constants/routes";

interface ErrorCardProps {
  error: string;
  errorCode?: number;
  title?: string;
  onRetry?: () => void;
  incidentId?: string;
}

const ErrorCard: React.FC<ErrorCardProps> = ({
  error,
  errorCode,
  title,
  onRetry,
  incidentId,
}) => {
  const theme = useTheme();
  const { logout } = useAuth();

  const handleSignIn = () => {
    logout();
    window.location.href = AUTH.LOGIN.path;
  };

  const handleCopyIncident = () => {
    if (incidentId) void navigator.clipboard.writeText(incidentId);
  };

  const isUnauthorized = errorCode === 401;
  const displayTitle =
    title ?? (isUnauthorized ? "Session expired" : "Unable to load resource");

  return (
    <Stack margin={2}>
      <Card
        sx={{
          borderRadius: 2,
          border: `1px solid ${alpha(theme.palette.divider, 0.06)}`,
          boxShadow: "none",
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Stack direction="row" spacing={1.5} alignItems="flex-start">
            <ErrorOutlineIcon
              sx={{
                fontSize: 24,
                color: "error.main",
                flexShrink: 0,
                mt: 0.25,
              }}
            />
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                {displayTitle}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {error}
              </Typography>

              <Stack direction="row" spacing={1} alignItems="center">
                {isUnauthorized ? (
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={handleSignIn}
                  >
                    Sign back in
                  </Button>
                ) : (
                  onRetry && (
                    <Button
                      variant="outlined"
                      color="primary"
                      size="small"
                      onClick={onRetry}
                    >
                      Retry
                    </Button>
                  )
                )}
                <Link
                  href="mailto:support@rusha.dev"
                  variant="body2"
                  color="text.secondary"
                  underline="hover"
                >
                  Contact Support
                </Link>
              </Stack>

              {incidentId && (
                <Stack
                  direction="row"
                  spacing={0.5}
                  alignItems="center"
                  sx={{ mt: 2 }}
                >
                  <Typography
                    variant="caption"
                    fontFamily="monospace"
                    color="text.secondary"
                  >
                    Incident: {incidentId}
                  </Typography>
                  <Tooltip title="Copy incident ID">
                    <IconButton
                      size="small"
                      aria-label="Copy incident ID"
                      onClick={handleCopyIncident}
                      sx={{ p: 0.5 }}
                    >
                      <Typography variant="caption" color="primary">
                        copy
                      </Typography>
                    </IconButton>
                  </Tooltip>
                </Stack>
              )}
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  );
};

export default ErrorCard;
